import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Search, Trash2, ArrowLeft, MapPin, DollarSign, Clock, User } from 'lucide-react';
import { SERVICE_CATEGORIES } from '../constants/categories';

const AdminJobManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, API, language } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const content = {
    en: {
      title: 'Job Management',
      back: 'Back',
      searchPlaceholder: 'Search by title or description...',
      allStatus: 'All Status',
      open: 'Open',
      assigned: 'Assigned',
      completed: 'Completed',
      allCategories: 'All Categories',
      totalJobs: 'Total Jobs',
      title: 'Title',
      category: 'Category',
      client: 'Client',
      status: 'Status',
      budget: 'Budget',
      bids: 'Bids',
      professional: 'Professional',
      actions: 'Actions',
      delete: 'Delete',
      deleteTitle: 'Delete Job',
      deleteMessage: 'Are you sure you want to delete this job? This will also delete all bids and reviews.',
      cancel: 'Cancel',
      confirm: 'Confirm Delete',
      urgency: 'Urgency',
      low: 'Low',
      medium: 'Medium',
      high: 'High'
    },
    de: {
      title: 'Auftragsverwaltung',
      back: 'Zurück',
      searchPlaceholder: 'Nach Titel oder Beschreibung suchen...',
      allStatus: 'Alle Status',
      open: 'Offen',
      assigned: 'Zugewiesen',
      completed: 'Abgeschlossen',
      allCategories: 'Alle Kategorien',
      totalJobs: 'Gesamtaufträge',
      title: 'Titel',
      category: 'Kategorie',
      client: 'Kunde',
      status: 'Status',
      budget: 'Budget',
      bids: 'Angebote',
      professional: 'Fachkraft',
      actions: 'Aktionen',
      delete: 'Löschen',
      deleteTitle: 'Auftrag löschen',
      deleteMessage: 'Möchten Sie diesen Auftrag wirklich löschen? Dabei werden auch alle Angebote und Bewertungen gelöscht.',
      cancel: 'Abbrechen',
      confirm: 'Löschen bestätigen',
      urgency: 'Dringlichkeit',
      low: 'Niedrig',
      medium: 'Mittel',
      high: 'Hoch'
    }
  };

  const t = content[language] || content.en;

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API}/admin/jobs?${params.toString()}`);
      setJobs(response.data.jobs);
    } catch (error) {
      toast.error('Failed to load jobs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchJobs();
    }, 500);
    return () => clearTimeout(debounce);
  }, [searchQuery, statusFilter, categoryFilter]);

  const handleDeleteJob = async () => {
    try {
      await axios.delete(`${API}/admin/jobs/${selectedJob.id}`);
      toast.success('Job deleted successfully');
      setDeleteDialogOpen(false);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    }
  };

  const openDeleteDialog = (job) => {
    setSelectedJob(job);
    setDeleteDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-blue-600',
      assigned: 'bg-orange-600',
      completed: 'bg-green-600'
    };
    return <Badge className={colors[status] || 'bg-gray-600'}>{t[status] || status}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[urgency] || 'bg-gray-100'}>{t[urgency] || urgency}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Briefcase className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold font-heading">{t.title}</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t.totalJobs}: {jobs.length}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allStatus}</SelectItem>
                  <SelectItem value="open">{t.open}</SelectItem>
                  <SelectItem value="assigned">{t.assigned}</SelectItem>
                  <SelectItem value="completed">{t.completed}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allCategories}</SelectItem>
                  {SERVICE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {language === 'de' ? cat.label_de : cat.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Jobs Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} className="hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      {/* Title and Badges */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{job.title}</h3>
                          <div className="flex flex-wrap gap-2">
                            {getStatusBadge(job.status)}
                            {getUrgencyBadge(job.urgency)}
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Briefcase className="w-3 h-3 mr-1" />
                              {SERVICE_CATEGORIES.find(c => c.value === job.category)?.[language === 'de' ? 'label_de' : 'label_en'] || job.category}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <p className="text-gray-600 mb-4 line-clamp-2 text-sm leading-relaxed">{job.description}</p>
                      
                      {/* Info Grid */}
                      <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-white rounded-lg">
                            <User className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t.client}</p>
                            <p className="text-sm font-semibold text-gray-900">{job.client_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-white rounded-lg">
                            <DollarSign className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t.budget}</p>
                            <p className="text-sm font-semibold text-green-600">CHF {job.budget}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-white rounded-lg">
                            <Briefcase className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t.bids}</p>
                            <p className="text-sm font-semibold text-blue-600">{job.bids_count}</p>
                          </div>
                        </div>
                        
                        {job.assigned_professional && (
                          <div className="flex items-center gap-2">
                            <div className="p-2 bg-white rounded-lg">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t.professional}</p>
                              <p className="text-sm font-semibold text-purple-600">{job.assigned_professional.name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Delete Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 hover:border-red-300 border-red-200 shrink-0"
                      onClick={() => openDeleteDialog(job)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {t.delete}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.deleteTitle}</DialogTitle>
            <DialogDescription>{t.deleteMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t.cancel}
            </Button>
            <Button variant="destructive" onClick={handleDeleteJob}>
              {t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobManagement;
